// Totales de las subtablas de líneas: las que se despliegan dentro de un pedido.
//
// El pie de la tabla maestra (TableTotalsFooter + useColumnAggregations) totaliza
// las CABECERAS. Al desplegar un pedido, las líneas quedaban sin sumar y había
// que echar cuentas a mano. Aquí vive la parte que se puede probar sin render:
// qué columnas suman y de dónde sale cada número.
//
// El cálculo se delega en `computeAggregations`, el mismo del pie de arriba, para
// que el desglose por divisa y el tratamiento de los huecos sean idénticos.
//
// **Qué NO se suma.** Sólo entran las columnas aditivas. El precio unitario y el
// descuento se quedan fuera a propósito: la suma de los precios de cinco
// artículos distintos no significa nada, y la de un porcentaje, menos. Un total
// ahí no daría error, daría una cifra con pinta de buena.
import type { AggColumn, AggResult, PedidoCompraLinea, PedidoVentaLinea } from '@/types'
import { computeAggregations } from './columnAggregations'

/** Una columna del pie, en el MISMO orden que la cabecera de la subtabla.
 *
 *  Sin `agg` la columna no suma: sólo ocupa su hueco para que la rejilla cuadre.
 *  El formateador se inyecta desde la vista, que es quien sabe cómo se escribe
 *  cada cifra en sus celdas — un total con otro formato que la columna que
 *  remata se lee como si fuera otra magnitud. */
export interface ColumnaPie<T> {
  key: string
  agg?: AggColumn<T>
  format?: (valor: number) => string
}

type Formateador = (valor: number) => string

/** Suma las columnas que declaren de dónde sale el número. */
export function subtotales<T>(
  rows: T[],
  columnas: ColumnaPie<T>[],
): Record<string, AggResult> {
  const conNumero = columnas
    .map(c => c.agg)
    .filter((a): a is AggColumn<T> => !!a)
  const estado = Object.fromEntries(conNumero.map(a => [a.key, 'sum' as const]))
  return computeAggregations(rows, conNumero, estado)
}

/** Columnas del pie de las líneas de un PEDIDO DE VENTA.
 *
 *  `pendiente` es la misma función que pinta la columna: en la banda sin facturar
 *  es lo que queda por lanzar a cargas y en una factura lo que le queda a la
 *  línea por facturar. Se inyecta en vez de recalcularse para que el total no
 *  pueda contradecir a la columna que remata.
 *
 *  Ojo con `qtyLinea` y `qtySinFacturar`: son cifras de nivel LÍNEA, iguales en
 *  todas las filas de esa línea. Sumarlas es correcto AQUÍ porque cada banda
 *  contiene como mucho una fila por línea, pero un pie único para todo el pedido
 *  las contaría una vez por factura. Por eso hay un total por banda y no uno
 *  general. */
export function columnasPieVenta({
  esSinFacturar, pendiente, fmtCantidad, fmtImporte,
}: {
  esSinFacturar: boolean
  pendiente: (linea: PedidoVentaLinea) => number
  fmtCantidad: Formateador
  fmtImporte: Formateador
}): ColumnaPie<PedidoVentaLinea>[] {
  return [
    { key: 'numLinea' },
    { key: 'articulo' },
    { key: 'familia' },
    { key: 'qtyLinea', agg: { key: 'qtyLinea', valueKey: 'qtyLinea' }, format: fmtCantidad },
    { key: 'qtyPedida', agg: { key: 'qtyPedida', valueKey: 'qtyPedida' }, format: fmtCantidad },
    { key: 'avance' },
    { key: 'qtyServida', agg: { key: 'qtyServida', valueKey: 'qtyServida' }, format: fmtCantidad },
    {
      key: 'pendiente',
      agg: {
        key: 'pendiente',
        value: (l) => (esSinFacturar ? pendiente(l) : l.qtySinFacturar),
      },
      format: fmtCantidad,
    },
    { key: 'um' },
    // Precio unitario y descuento: sin total a propósito (ver cabecera).
    { key: 'precioUnitario' },
    { key: 'descuento' },
    {
      key: 'importeNeto',
      agg: { key: 'importeNeto', valueKey: 'importeNeto', currencyKey: 'moneda', decimals: 2 },
      format: fmtImporte,
    },
    {
      key: 'importePendiente',
      agg: {
        key: 'importePendiente', valueKey: 'importePendiente',
        currencyKey: 'moneda', decimals: 2,
      },
      format: fmtImporte,
    },
    { key: 'estado' },
    { key: 'fechaEntrega' },
    { key: 'fechaEnvio' },
  ]
}

/** Columnas del pie de las líneas de un PEDIDO DE COMPRA. */
export function columnasPieCompra({
  fmtCantidad, fmtImporte,
}: {
  fmtCantidad: Formateador
  fmtImporte: Formateador
}): ColumnaPie<PedidoCompraLinea>[] {
  return [
    { key: 'numLinea' },
    { key: 'articulo' },
    { key: 'familia' },
    { key: 'qtyPedida', agg: { key: 'qtyPedida', valueKey: 'qtyPedida' }, format: fmtCantidad },
    { key: 'qtyRecibida', agg: { key: 'qtyRecibida', valueKey: 'qtyRecibida' }, format: fmtCantidad },
    { key: 'qtyPendiente', agg: { key: 'qtyPendiente', valueKey: 'qtyPendiente' }, format: fmtCantidad },
    { key: 'um' },
    { key: 'precioUnitario' },
    {
      key: 'importePendiente',
      agg: {
        key: 'importePendiente', valueKey: 'importePendiente',
        currencyKey: 'moneda', decimals: 2,
      },
      format: fmtImporte,
    },
    { key: 'estado' },
    { key: 'fechaEntrega' },
    { key: 'fechaConfirmada' },
  ]
}
